import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertCircle, CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import { cn } from './cn.js'

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: number
  tone: ToastTone
  title: string
  description?: string
}

interface ToastContextValue {
  toast: (tone: ToastTone, title: string, description?: string) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const config: Record<ToastTone, { cls: string; Icon: typeof Info }> = {
  success: { cls: 'border-success/30 bg-success-subtle text-success', Icon: CheckCircle2 },
  error: { cls: 'border-danger/30 bg-danger-subtle text-danger', Icon: AlertCircle },
  warning: { cls: 'border-warning/30 bg-warning-subtle text-warning', Icon: TriangleAlert },
  info: { cls: 'border-info/30 bg-info-subtle text-info', Icon: Info },
}

const DURATION_MS = 4500

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const { cls, Icon } = config[toast.tone]

  useEffect(() => {
    const id = setTimeout(() => onDismiss(toast.id), DURATION_MS)
    return () => clearTimeout(id)
  }, [toast.id, onDismiss])

  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'pointer-events-auto flex w-80 items-start gap-3 rounded-xl border bg-surface p-3.5 shadow-lg',
        'animate-[slide-up_0.22s_var(--ease-out-quint)]',
        cls,
      )}
    >
      <Icon className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-xs text-fg-muted">{toast.description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="-me-1 -mt-1 rounded p-1 text-fg-subtle transition-colors hover:text-fg"
        aria-label="إغلاق"
      >
        <X className="size-3.5" />
      </button>
    </div>
  )
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback((tone: ToastTone, title: string, description?: string) => {
    // مُعرّف تصاعدي بسيط — يكفي لعمر الصفحة
    setToasts((prev) => [...prev.slice(-4), { id: Date.now() + prev.length, tone, title, description }])
  }, [])

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (title, description) => toast('success', title, description),
      error: (title, description) => toast('error', title, description),
    }),
    [toast],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 start-4 z-[100] flex flex-col gap-2"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast يجب أن يُستعمل داخل ToastProvider')
  return ctx
}
